<script lang="ts" setup>
import feedbackSuccessIllustration from '~/assets/images/support/feedback-success.svg';
import { PageHero } from '~/components';
import {
  AppBreadcrumbs,
  AppButton,
  AppLink,
  AppTextarea,
  AppTextInput,
  AppTypography,
} from '~/components/base';
import { useContact } from '~/composables/useContact';
import ContactPage from '~/utils/contents/pages/ContactPage';

definePageMeta({ layout: 'default' });

useHead({ title: 'Contact us' });
useSeoMeta({
  description:
    'Have a question about VoteWise? Contact the team and we will get back to you.',
  ogTitle: 'Contact us | VoteWise',
  ogDescription:
    'Have a question about VoteWise? Contact the team and we will get back to you.',
});

const content = ContactPage;

const breadcrumbs = [
  { label: 'Support', to: '/support' },
  { label: 'Contact us' },
];

const {
  form,
  pending,
  isSuccess,
  isError,
  errorMessage,
  fieldErrors,
  submit,
  clearError,
} = useContact();

// VTW-439: the client asked for a low-friction form — only Message is
// mandatory, matching the API's validation, so the CTA unlocks as soon as a
// message has been typed. Empty name/email fields are sent as blanks and the
// API's ConvertEmptyStringsToNull turns them into nulls.
const canSubmit = computed(() => !!form.message.trim());

const optionalLabel = (label: string): string =>
  `${label} ${content.optionalSuffix}`;

async function submitContact(): Promise<void> {
  if (!canSubmit.value || pending.value) return;
  await submit();
}
</script>

<template>
  <div class="contact-us-page">
    <PageHero constrained max-width="max-w-3xl">
      <div data-testid="contact-us-page">
        <AppBreadcrumbs
          :custom-breadcrumbs="breadcrumbs"
          class="hidden px-0 lg:flex"
        />

        <div class="contact-us-page__card">
          <AppTypography
            type="title-5"
            element="h1"
            class="text-color-primary mb-6 text-left lg:block lg:text-center"
          >
            {{ content.heading }}
          </AppTypography>

          <template v-if="!isSuccess">
            <form class="contact-us-page__form" @submit.prevent="submitContact">
              <div class="contact-us-page__names">
                <AppTextInput
                  v-model="form.firstName"
                  :label="optionalLabel(content.fields.firstName.label)"
                  :error-message="fieldErrors.first_name"
                  :invalid="!!fieldErrors.first_name"
                  :disabled="pending"
                  data-testid="contact-us-first-name"
                  @input="clearError"
                />

                <AppTextInput
                  v-model="form.lastName"
                  :label="optionalLabel(content.fields.lastName.label)"
                  :error-message="fieldErrors.last_name"
                  :invalid="!!fieldErrors.last_name"
                  :disabled="pending"
                  data-testid="contact-us-last-name"
                  @input="clearError"
                />
              </div>

              <AppTextInput
                v-model="form.email"
                type="email"
                :label="optionalLabel(content.fields.email.label)"
                :error-message="fieldErrors.email_address"
                :invalid="!!fieldErrors.email_address"
                :disabled="pending"
                data-testid="contact-us-email"
                @input="clearError"
              />

              <div class="contact-us-page__textarea">
                <AppTextarea
                  v-model="form.message"
                  :label="content.fields.message.label"
                  :rows="6"
                  :maxlength="content.messageMaxLength"
                  :error-message="fieldErrors.message"
                  :invalid="!!fieldErrors.message"
                  :disabled="pending"
                  data-testid="contact-us-message"
                  @input="clearError"
                />
                <AppTypography
                  type="caption"
                  class="contact-us-page__counter text-color-tertiary"
                >
                  {{ form.message.length }} / {{ content.messageMaxLength }}
                </AppTypography>
              </div>

              <AppTypography
                v-if="isError && errorMessage"
                type="footnote"
                class="contact-us-page__error text-color-danger"
                data-testid="contact-us-error"
              >
                {{ errorMessage }}
              </AppTypography>

              <AppButton
                type="submit"
                :label="pending ? content.submittingLabel : content.submitLabel"
                data-testid="contact-us-submit"
                class="contact-us-page__submit"
                :disabled="!canSubmit || pending"
                :loading="pending"
              />

              <AppTypography
                element="p"
                type="footnote"
                class="contact-us-page__consent"
                data-testid="contact-us-consent"
              >
                {{ content.consent.prefix
                }}<AppLink
                  :label="content.consent.linkLabel"
                  to="/privacy-policy"
                />{{ content.consent.suffix }}
              </AppTypography>
            </form>
          </template>

          <template v-else>
            <div
              class="contact-us-page__success"
              data-testid="contact-us-success"
            >
              <img
                :src="feedbackSuccessIllustration"
                alt=""
                width="241"
                height="180"
                class="contact-us-page__success-illustration"
              />
              <AppTypography
                type="title-6"
                element="h2"
                class="text-color-primary"
              >
                {{ content.successTitle }}
              </AppTypography>
              <AppTypography
                type="body"
                class="contact-us-page__success-subtitle"
              >
                {{ content.successMessage }}
              </AppTypography>
              <NuxtLink to="/" class="contact-us-page__home-link">
                <AppButton label="Back to Homepage" />
              </NuxtLink>
            </div>
          </template>
        </div>
      </div>
    </PageHero>
  </div>
</template>

<style scoped lang="scss">
// Deliberately mirrors report-a-problem.vue: both are Help Centre leaf pages
// reached from a /support row, and the client asked for one shared frame.
.contact-us-page {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: calc(100vh - var(--header-height));

  &__card {
    margin-top: $spacing-md;

    @media (min-width: 1024px) {
      padding: $spacing-2xl;
      border-radius: $radius-2xl;
      background-color: $surface-color-bg;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: $spacing-lg;

    // Figma: every field on these Help Centre forms is radius/lg (16px).
    // PrimeVue's default is 6px. Scoped to this page rather than changed on
    // `.p-inputtext` globally, which would restyle every form in the app.
    :deep(.p-inputtext),
    :deep(.p-textarea) {
      border-radius: $radius-lg;
    }

    // Stretch the floating-label fields to fill the form width. Without this
    // the PrimeVue inputs shrink to their intrinsic width and look truncated.
    :deep(.app-floating-label-wrapper),
    :deep(.p-floatlabel) {
      display: block;
      width: 100%;
    }

    :deep(.app-input-text),
    :deep(.app-textarea) {
      width: 100%;
    }
  }

  // First and last name share a row on tablet up, matching the landing
  // contact form. They stack below 640px so the fields never get too narrow
  // to read the floating label inside them.
  &__names {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: $spacing-lg;

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  }

  &__textarea {
    position: relative;
  }

  &__counter {
    position: absolute;
    right: $spacing-md;
    bottom: $spacing-md;
    pointer-events: none;
  }

  &__submit {
    width: 100%;

    :deep(.p-button) {
      width: 100%;
      justify-content: center;
    }
  }

  // VTW-439 asks for the privacy-policy note directly below the Send Message
  // button. Underlined rather than colour-only so the link is distinguishable
  // without relying on hue.
  &__consent {
    text-align: center;

    :deep(.app-link) {
      text-decoration: underline;
    }
  }

  &__success {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-xs;
    padding: $spacing-2xl 0;
    text-align: center;
  }

  // Figma: text/primary (#201e1a) — this app's `.text-color-primary` token is a
  // cooler near-black (#050529), so the warm value is set here as the other
  // Figma-matched cards do.
  &__success-subtitle {
    color: #201e1a;
  }

  &__success-illustration {
    width: 241px;
    max-width: 100%;
    height: auto;
    margin-bottom: $spacing-2xl;
  }

  &__home-link {
    margin-top: $spacing-2xl;
    text-decoration: none;
  }
}
</style>
